import { graphql } from "gatsby"
import { default as _BackgroundImage } from "gatsby-background-image"
import { FluidObject } from "gatsby-image"
import { navigate } from "gatsby-link"
import {
  ContentfulRichTextGatsbyReference,
  RenderRichTextData,
} from "gatsby-source-contentful/rich-text"
import React, { useContext, useState } from "react"
import { scroller } from "react-scroll"
import styled, { css } from "styled-components"
import Breakpoints from "../../components/shared/breakpoints"
import { UIContext } from "../../components/shared/layout"
import { default as _IndustryChevron } from "../../components/shared/svg/industry-chevron"
import Button from "../atoms/button"
import { Spacer } from "../atoms/layout-primitives"
import { RichText, T, Text } from "../atoms/text"
import VC from "../atoms/variant-container"
import { normalizeForwardSlashes } from "../util/internal-routes"
import withVariants from "../util/withVariants"

export const INDUSTRY_CARD_TYPENAME = "ContentfulIndustryCard"

export interface IIndustryCard {
  __typename: typeof INDUSTRY_CARD_TYPENAME
  image: {
    fluid: FluidObject
  }
  title: string
  body: RenderRichTextData<ContentfulRichTextGatsbyReference> | string
  linkedPage: {
    slug: string
  }
}

const BackgroundImage = withVariants(styled(_BackgroundImage)``)
const IndustryChevron = withVariants(_IndustryChevron)

const Container = styled.div<{ $isExpanded?: boolean }>`
  all: unset;
  border-radius: ${(props) => (props.$isExpanded ? "4px 4px 0px 0px" : "4px")};
  box-shadow: ${(props) =>
    props.$isExpanded ? "0px 4px 20px rgba(0, 0, 0, 0.07)" : "none"};
  overflow: hidden;
  user-select: none;

  @media (min-width: ${Breakpoints.md}) {
    border-radius: 8px;
    box-shadow: 0px 4px 20px rgba(0, 0, 0, 0.07);

    cursor: pointer;

    @media (pointer: fine) {
      &,
      & * {
        transition: box-shadow, transform 0.33s ease-out;
      }

      :hover {
        box-shadow: 0px 4px 24px rgba(0, 0, 0, 0.12);
        .industry-card--image {
          transform: scale(1.1);
        }
      }
    }
  }
`

const IndustryCard = ({
  image,
  title,
  body,
  linkedPage,
}: Omit<IIndustryCard, "__typename">) => {
  const {
    state: { breakpoint },
  } = useContext(UIContext)

  const [isExpanded, setIsExpanded] = useState(false)
  const toggleExpanded = () => setIsExpanded((x) => !x)
  const linkUrl = normalizeForwardSlashes(linkedPage?.slug)
  const contactScroll = linkUrl === "#contact-form"

  const containerProps = breakpoint !== "sm" && {
    role: "link",
    onClick: () => {
      contactScroll
        ? scroller.scrollTo("contact-form", {
            duration: 800,
            delay: 0,
            smooth: "easeInOutQuart",
          })
        : navigate(linkUrl)
    },
  }

  const toggleARIAProps = breakpoint === "sm" && {
    role: "button",
    ["aria-expanded"]: isExpanded,
    // ["aria-controls"]: `industry-card-collapse--${link}`,
  }

  return (
    <Container $isExpanded={isExpanded} {...containerProps}>
      {/* Image Container */}
      <VC
        {...toggleARIAProps}
        onClick={toggleExpanded}
        variants={[
          css`
            cursor: pointer;
            height: 185px;
            @supports (aspect-ratio: 16 / 9) {
              height: unset;
              aspect-ratio: 16 / 9;
            }
            overflow: hidden;
          `,
          css`
            height: 141px;
            @supports (aspect-ratio: 21 / 9) {
              height: unset;
              aspect-ratio: 21 / 9;
            }
          `,
        ]}
      >
        <BackgroundImage
          className="industry-card--image"
          {...image}
          variants={[
            css`
              height: 100%;
              display: grid;
              grid-template-columns: 1fr;
              grid-template-rows: 1fr;
              overflow: hidden;
              background-position: center;
              background-size: cover;
            `,
          ]}
        >
          {/* Linear Gradiant Overlay */}
          <VC
            variants={[
              css`
                background: linear-gradient(
                  180deg,
                  rgba(40, 40, 40, 0) 50%,
                  #1a1a1a 91.04%
                );
                height: 100%;
                grid-row: 1;
                grid-column: 1;
              `,
              css`
                display: none;
              `,
            ]}
          ></VC>

          {/* Card Image Content */}
          <VC
            variants={[
              css`
                height: 100%;
                display: flex;
                align-items: flex-end;
                justify-content: center;
                z-index: 10;
                grid-row: 1;
                grid-column: 1;
              `,
              css`
                display: none;
              `,
            ]}
          >
            <VC
              variants={[
                css`
                  display: flex;
                  flex-direction: column;
                  align-items: center;
                  padding: 12px;
                `,
              ]}
            >
              <Text
                as="h4"
                variants={[
                  css`
                    ${T.sm.h4(400)};
                    color: var(--white);
                    margin-bottom: 12px;
                  `,
                ]}
              >
                {title}
              </Text>
              <IndustryChevron
                color={isExpanded ? "transparent" : "var(--white)"}
              />
            </VC>
          </VC>
        </BackgroundImage>
      </VC>

      {/* Card Body */}
      <VC
        // id={`industry-card-collapse--${link}`}
        variants={[
          css`
            padding: 24px 20px;
            text-align: center;
            display: ${isExpanded ? "block" : "none"};
          `,
          css`
            display: block;
            padding: 32px 24px;
          `,
          css`
            display: block;
            padding: 48px 32px;
          `,
        ]}
      >
        <Text
          as="h4"
          variants={[
            css`
              display: none;
            `,
            css`
              display: block;
              ${T.md.h3(500)};
              margin-bottom: 16px;
            `,
            css`
              ${T.lg.h4(500)};
            `,
          ]}
        >
          {title}
        </Text>
        {typeof body === "string" ? (
          <Text.Body>{body}</Text.Body>
        ) : (
          <RichText>{body}</RichText>
        )}
        <Spacer height={{ sm: "32px", md: "0" }} />
        {contactScroll ? (
          <Button.ScrollLink
            fullWidth
            outline="true"
            to="contact-form"
            smooth
            duration={1000}
            variants={{
              md: css`
                && {
                  display: none;
                }
              `,
            }}
          >
            Learn More
          </Button.ScrollLink>
        ) : (
          <Button.Link
            fullWidth
            outline="true"
            to={linkUrl}
            variants={{
              md: css`
                && {
                  display: none;
                }
              `,
            }}
          >
            Learn More
          </Button.Link>
        )}
        <Spacer height={{ sm: "32px", md: "0" }} />
        <IndustryChevron
          {...toggleARIAProps}
          color="#2e2e2e4d"
          transform="rotate(180)"
          onClick={toggleExpanded}
          variants={{ sm: { cursor: "pointer" }, md: { display: "none" } }}
        />
      </VC>
    </Container>
  )
}

export const IndustryCardFragment = graphql`
  fragment ContentfulIndustryCard on ContentfulIndustryCard {
    contentful_id
    image {
      fluid(quality: 90) {
        ...GatsbyContentfulFluid_withWebp
      }
    }
    title
    body {
      raw
    }
    linkedPage {
      slug
    }
  }
`
export default IndustryCard
